import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Dumbbell, Globe } from 'lucide-react';
import { useMemo, useState } from 'react';

import { usePagination } from '../../shared/hooks/usePagination';
import { Pagination } from '../../shared/ui/Pagination';
import { SearchInput } from '../../shared/ui/SearchInput';
import { Skeleton } from '../../shared/ui/Skeleton';
import { SkillFilter, type SkillFilterOption } from '../../shared/ui/SkillFilter';

import {
  listPublishedTests,
  PRACTICE_QUERY_KEYS,
  type PracticeSectionKind,
  type PublishedTest,
} from './practice-api';
import s from './PracticeTestsCard.module.css';
import { StartOverDialog } from './StartOverDialog';
import { useStartPractice } from './useStartPractice';

const SECTION_LABEL = new Map<PracticeSectionKind, string>([
  ['listening', 'Listening'],
  ['reading', 'Reading'],
  ['writing', 'Writing'],
  ['speaking', 'Speaking'],
]);
const SECTION_ORDER: PracticeSectionKind[] = ['listening', 'reading', 'writing', 'speaking'];
/** Three plates where the first page of rows will land. */
const SKELETON_ROWS = ['one', 'two', 'three'] as const;
const SKILL_FILTER_OPTIONS: SkillFilterOption<PracticeSectionKind>[] = SECTION_ORDER.map(
  (kind) => ({ value: kind, label: SECTION_LABEL.get(kind) ?? kind }),
);

function orderedKinds(kinds: PracticeSectionKind[]): PracticeSectionKind[] {
  return SECTION_ORDER.filter((k) => kinds.includes(k));
}

export function PracticeTestsCard(): JSX.Element {
  const [query, setQuery] = useState('');
  const [skills, setSkills] = useState<ReadonlySet<PracticeSectionKind>>(new Set());

  const tests = useQuery({
    queryKey: PRACTICE_QUERY_KEYS.publishedTests,
    queryFn: listPublishedTests,
  });

  // Start resumes an unfinished attempt rather than creating a second one, so
  // the flow asks Continue-or-Start-over when the server hands one back.
  const flow = useStartPractice();

  function toggleSkill(kind: PracticeSectionKind): void {
    setSkills((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }

  const allTests = useMemo(() => tests.data ?? [], [tests.data]);

  const filteredTests = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return allTests.filter((t) => {
      const titleMatch = needle === '' || t.title.toLowerCase().includes(needle);
      const skillMatch = skills.size === 0 || t.section_kinds.some((k) => skills.has(k));
      return titleMatch && skillMatch;
    });
  }, [allTests, query, skills]);

  const pages = usePagination(filteredTests);

  if (tests.isPending) {
    return (
      <div className={s.list} role="status" aria-label="Loading practice tests">
        {SKELETON_ROWS.map((row) => (
          <Skeleton key={row} height={64} radius="lg" />
        ))}
      </div>
    );
  }

  if (tests.error !== null) {
    return <p className={s.note}>Could not load practice tests: {tests.error.message}</p>;
  }

  if (allTests.length === 0) {
    return (
      <div className={s.empty}>
        <span className={s.emptyArt} aria-hidden="true">
          <Dumbbell size={24} />
        </span>
        <div>
          <h3 className={s.emptyTitle}>No practice tests yet</h3>
          <p className={s.emptyBody}>
            Published tests from your institute (and our shared bank) appear here so you can take a
            full mock any time — no scheduling needed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={s.card}>
      {flow.error !== null ? (
        <p className={s.error} role="alert">
          {flow.error}
        </p>
      ) : null}
      <div className={s.controls}>
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Search practice tests…"
          label="Search practice tests by title"
        />
        <SkillFilter
          options={SKILL_FILTER_OPTIONS}
          selected={skills}
          onToggle={toggleSkill}
          label="Filter practice tests by skill"
        />
      </div>
      {filteredTests.length === 0 ? (
        <p className={s.note}>
          No practice tests match{' '}
          {query.trim() !== '' ? `“${query.trim()}”` : 'the selected skills'}. Try clearing the
          search or a filter.
        </p>
      ) : (
        <>
          <ul className={s.list}>
            {pages.pageItems.map((t) => (
              <PracticeRow
                key={t.id}
                test={t}
                starting={flow.pendingTestId === t.id}
                disabled={flow.pendingTestId !== null}
                onStart={() => flow.start(t.id, t.title)}
              />
            ))}
          </ul>
          <Pagination
            page={pages.page}
            pageCount={pages.pageCount}
            onPageChange={pages.setPage}
            label="Practice test pages"
            summary={`${String(pages.rangeStart)}–${String(pages.rangeEnd)} of ${String(pages.total)} tests`}
          />
        </>
      )}
      <StartOverDialog {...flow.dialogProps} />
    </div>
  );
}

interface RowProps {
  test: PublishedTest;
  starting: boolean;
  disabled: boolean;
  onStart: () => void;
}

function PracticeRow({ test, starting, disabled, onStart }: RowProps): JSX.Element {
  return (
    <li className={s.row}>
      <div className={s.rowMain}>
        <h3 className={s.rowTitle}>{test.title}</h3>
        <div className={s.rowMeta}>
          <span className={s.examChip}>
            {test.exam_type === 'academic' ? 'Academic' : 'General'}
          </span>
          <span className={s.metaDot} aria-hidden="true">
            ·
          </span>
          <span className={s.duration}>{test.total_duration_minutes} min</span>
          {test.is_global ? (
            <span className={s.globalBadge}>
              <Globe size={12} aria-hidden="true" />
              Open to all
            </span>
          ) : null}
        </div>
        <ul className={s.kinds} aria-label="Sections">
          {orderedKinds(test.section_kinds).map((k) => (
            <li key={k} className={s.kindChip}>
              {SECTION_LABEL.get(k) ?? k}
            </li>
          ))}
        </ul>
      </div>
      <button
        type="button"
        className={s.startBtn}
        onClick={onStart}
        disabled={disabled}
        aria-busy={starting}
        aria-label={`Start practice: ${test.title}`}
      >
        {starting ? 'Starting…' : 'Start practice'}
        <ArrowRight size={14} aria-hidden="true" />
      </button>
    </li>
  );
}
